import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  selectHasStagedChanges,
  selectIsOwner,
  selectIsSaving,
  selectLatestStatus,
  selectModal,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { useIsWaitingForResponse } from '@verantos-dev/app-state-research-assistant';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { useSaveTimeoutNoticeStore } from './useSaveTimeoutNotice';
import {
  CohortBuilderActionDialogType,
  useCohortBuilderModalContext,
} from '../../contexts/CohortBuilderModalContext';
import { useEmitResearchAssistantUpdate } from '../../hooks/common/useEmitResearchAssistantUpdate';

export type StagedChangesPillMode = 'unsaved' | 'failure';

export interface UseStagedChangesPillResult {
  visible: boolean;
  mode: StagedChangesPillMode;
  isDisabled: boolean;
  onRevert: () => void;
  onSave: () => void;
  onRetry: () => void;
  onDismissFailure: () => void;
}

export const useStagedChangesPill = (): UseStagedChangesPillResult => {
  // TODO: cleanup with explicit_save ff
  const isExplicitSave = useFeatureFlagSplit('explicit_save');
  const hasStagedChanges = useCohortDefinitionStore(selectHasStagedChanges);
  // Raw ownership (independent of the AD lock): a non-owner must never see the
  // staged-changes pill — those are the owner's uncommitted edits, and the pill
  // exposes Revert/Save the non-owner has no right to (OR-6270). An owner of an
  // AD-locked cohort still sees their pill, so selectIsOwner (not selectIsEditable).
  const isOwner = useCohortDefinitionStore(selectIsOwner);
  const isSaving = useCohortDefinitionStore(selectIsSaving);
  const latestStatus = useCohortDefinitionStore(selectLatestStatus);
  const modal = useCohortDefinitionStore(selectModal);
  // Zustand RA signal (gen-3 sets only this); the legacy Redux selector stays
  // false under gen-3, which left the pill enabled while the agent was working.
  const isWaitingForRA = useIsWaitingForResponse();
  const { openDialog, activeDialog } = useCohortBuilderModalContext();
  const saveFailureOpen = useSaveTimeoutNoticeStore((s) => s.isOpen);
  const pendingPayload = useSaveTimeoutNoticeStore((s) => s.pendingPayload);
  const dismissFailure = useSaveTimeoutNoticeStore((s) => s.dismiss);
  const emitResearchAssistantUpdate = useEmitResearchAssistantUpdate();

  const mode: StagedChangesPillMode = saveFailureOpen ? 'failure' : 'unsaved';
  const visible = !!isExplicitSave && isOwner && (hasStagedChanges || saveFailureOpen);

  const isProcessing = latestStatus === CohortDefinitionStatus.PROCESSING;
  const isDisabled =
    isProcessing || !!isWaitingForRA || activeDialog !== null || modal !== undefined || isSaving;

  const onRetry = () => {
    if (pendingPayload) {
      emitResearchAssistantUpdate(pendingPayload);
    }
    dismissFailure();
  };

  return {
    visible,
    mode,
    isDisabled,
    onRevert: () => openDialog(CohortBuilderActionDialogType.REVERT_COHORT_DEFINITION),
    onSave: () => openDialog(CohortBuilderActionDialogType.SAVE_COHORT_DEFINITION),
    onRetry,
    onDismissFailure: dismissFailure,
  };
};
